/**
 * Router Middleware Manager
 * Flexible per-route middleware system
 * 
 * Usage in router/index.js:
 * import { setupRouterMiddleware, auth, guest, permission } from "@/middleware";
 * 
 */

import store from "@/stores";

/**
 * Authentication middleware
 * Protects routes that require authentication
 */
export function auth({ to, next }) {

  const isTokenValid=store.getters.isTokenValid;

  if (!isTokenValid) {
    // Clear user data and redirect to login
    store.state.user={};
    store.state.messages=[];
    store.state.notifications=[];
    store.state.isLoggedIn=false;

    return next({
      path: "/login",
      query: { redirect: to.fullPath }
    });
  }

  // Token is valid, set logged in state
  store.state.isLoggedIn=true;
  next();
}

/**
 * Permission middleware
 * Checks if user has required permissions for the route
 */
export function permission({ to, next }) {
  const hasPermission=store.state.user.permissions?.filter(
    p => p.component===to.name
  ).length>0;

  if (!hasPermission) {
    return next({
      path: "/401",
      query: { redirect: to.fullPath }
    });
  }

  next();
}

/**
 * Guest middleware
 * Redirects authenticated users away from guest-only pages
 */
export function guest({ next }) {
  const isTokenValid=store.getters.isTokenValid;

  if (isTokenValid) {
    // User is authenticated, redirect to dashboard
    return next({
      path: "/dashboard"
    });
  }

  next();
}

/**
 * Scroll to top middleware
 */
export function scrollToTop({ to,next }) {
  if (to.name!=="Messenger") {
    window.scrollTo(0, 0);
    next();
  }
}

/**
 * Clear search filters middleware
 */
export function clearSearchFilters({ to, next }) {
  if (to.name!=="homeBase"&&to.name!=="ProductsFilters") {
    store.state.searchFilter=[];
    store.state.selectedCategory="";
  }
  next();
}

/**
 * Product creation flow guard middleware
 * Ensures ProductCreatedSuccess page is only accessible from BoostPlanManagementAfterCreate
 */
export function productCreationFlowGuard({ to, from, next }) {
  if (to.name==='ProductCreatedSuccess'&&from.name!=='BoostPlanManagementAfterCreate') {
    return next({
      path: "/404",
      query: { redirect: to.fullPath }
    });
  }

  next();
}

/**
 * Middleware pipeline executor
 * Runs middleware functions in sequence
 */
function runMiddleware(context, middleware, index=0) {
  const nextMiddleware=middleware[index];

  if (!nextMiddleware) {
    // All middleware passed, proceed with navigation
    return context.next();
  }

  return nextMiddleware({
    ...context,
    next: (params) => {
      if (params) {
        // Middleware wants to redirect
        return context.next(params);
      }
      // Continue to next middleware
      return runMiddleware(context, middleware, index+1);
    }
  });
}

/**
 * Setup router middleware
 * Call this function in main.js after creating the router
 * 
 * @param {VueRouter} router - Vue Router instance
 */
export function setupRouterMiddleware(router) {
  router.beforeEach((to, from, next) => {
    // Global middleware that always runs
    const globalMiddleware=[scrollToTop, clearSearchFilters, productCreationFlowGuard];

    // Route-specific middleware
    const routeMiddleware=to.meta.middleware||[];

    // Combine global and route middleware
    const allMiddleware=[...globalMiddleware, ...routeMiddleware];

    // Create context object
    const context={ to, from, next };
    // console.log(to, from);

    // Run middleware pipeline
    return runMiddleware(context, allMiddleware);
  });
}

/**
 * Convenience function to combine multiple middleware
 */
export function combineMiddleware(...middleware) {
  return middleware;
}